import { useState, useCallback, useEffect, useRef } from "react"
import {X, ChevronLeft, ChevronRight, Check} from 'lucide-react'

const activities = [
  { src: "https://static.lumi.new/63/6338546d3c90216f0cbf7b01cd665bf8.webp", alt: "Como eu posso me regular — atividade de autorregulação emocional" },
  { src: "https://static.lumi.new/4c/4c04ea37fba2f28d73a071102455573d.webp", alt: "Jogo de pessoas e vínculos familiares" },
  { src: "https://static.lumi.new/09/09812083a6cd1e78741f958e15f9122f.webp", alt: "Jogo das emoções — ficha ilustrada" },
  { src: "https://static.lumi.new/52/52756c52a99c9435944923fe2ad34b32.webp", alt: "Cartas de expressões emocionais" },
  { src: "https://static.lumi.new/d8/d8e373fd8343ce21280bfec60a007188.webp", alt: "Roda das emoções" },
  { src: "https://static.lumi.new/3a/3af53c5e57841aeb6d916c095d866d55.png", alt: "Diário das emoções" },
  { src: "https://static.lumi.new/47/4751e3edec70f21cb1c072cb5c995499.png", alt: "Termômetro da raiva" },
  { src: "https://static.lumi.new/7f/7f23f1eb0b434a7028369482e45f6312.png", alt: "Caça-palavras das emoções" },
  { src: "https://static.lumi.new/77/771f32872a474be5c5a4030bc052d187.png", alt: "Minha rotina visual" },
  { src: "https://static.lumi.new/db/dbf02c0bafd1688184c505743413ee9a.png", alt: "Complete a frase — atividade projetiva" },
  { src: "https://static.lumi.new/ec/ece7b15b7ec2a6d86bc0da7afc05c3d3.png", alt: "Caixa de ferramentas emocional" },
  { src: "https://static.lumi.new/42/427982d7cabe2b52273e7def015fec9e.png", alt: "Desenhe sua família — atividade projetiva" },
  { src: "https://static.lumi.new/2a/2a1568f8c178eb080e1617fddfaea368.png", alt: "Jogo da autoestima" },
  { src: "https://static.lumi.new/37/37adadd62cc3b3f4998ce8271cd4ae3c.png", alt: "Cartas de respiração" },
  { src: "https://static.lumi.new/4d/4df1a9667a4d4cf4f93f0f4c5cc6172a.png", alt: "Meu círculo de apoio" },
]

const includedItems = [
  "Material 100% digital",
  "Acesso imediato",
  "Arquivos prontos para impressão",
  "Organização por categorias",
  "Atualizações inclusas",
]

export default function ActivitiesGallery() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const isPausedRef = useRef(false)

  const closeModal = useCallback(() => setActiveIndex(null), [])
  const showPrev = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i - 1 + activities.length) % activities.length)),
    []
  )
  const showNext = useCallback(
    () => setActiveIndex((i) => (i === null ? null : (i + 1) % activities.length)),
    []
  )

  useEffect(() => {
    if (activeIndex === null) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal()
      if (e.key === "ArrowLeft") showPrev()
      if (e.key === "ArrowRight") showNext()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [activeIndex, closeModal, showPrev, showNext])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let rafId: number
    const speed = 0.6

    const step = () => {
      if (!isPausedRef.current && activeIndex === null) {
        el.scrollLeft += speed
        const maxScroll = el.scrollWidth - el.clientWidth
        if (el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = 0
        }
      }
      rafId = requestAnimationFrame(step)
    }
    rafId = requestAnimationFrame(step)
    return () => cancelAnimationFrame(rafId)
  }, [activeIndex])

  return (
    <section className="py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-2">
          Veja algumas das atividades que você receberá
        </h2>
        <p className="text-center text-gray-500 mb-10 max-w-xl mx-auto">
          Mais de 140 atividades terapêuticas prontas para imprimir e aplicar no atendimento infantil.
        </p>

        <div className="-mx-6 px-6 sm:mx-0 sm:px-0">
          <div
            ref={scrollRef}
            onMouseEnter={() => { isPausedRef.current = true }}
            onMouseLeave={() => { isPausedRef.current = false }}
            onTouchStart={() => { isPausedRef.current = true }}
            onTouchEnd={() => { isPausedRef.current = false }}
            className="flex gap-4 overflow-x-auto pb-4 snap-start scrollbar-hide sm:grid sm:grid-cols-3 lg:grid-cols-4 sm:overflow-visible sm:pb-0"
          >
            {activities.map((item, index) => (
              <button
                key={item.src}
                onClick={() => setActiveIndex(index)}
                className="group relative flex-shrink-0 w-40 sm:w-auto snap-start rounded-xl overflow-hidden shadow-md border border-gray-100 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                aria-label={`Ampliar imagem: ${item.alt}`}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  className="w-full h-full aspect-[3/4] object-cover rounded-xl transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Highlight box */}
        <div className="mt-10 bg-teal-50 border border-teal-100 rounded-2xl p-6 sm:p-7">
          <div className="grid sm:grid-cols-2 gap-3">
            {includedItems.map((item) => (
              <div key={item} className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-white" />
                </div>
                <span className="text-gray-700 text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox / Modal */}
      {activeIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={closeModal}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
            aria-label="Fechar"
          >
            <X size={22} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation()
              showPrev()
            }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
            aria-label="Imagem anterior"
          >
            <ChevronLeft size={24} />
          </button>

          <img
            src={activities[activeIndex].src}
            alt={activities[activeIndex].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain"
          />

          <button
            onClick={(e) => {
              e.stopPropagation()
              showNext()
            }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
            aria-label="Próxima imagem"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </section>
  )
}
